import { useMemo, useState } from "react";
import { SalesDeskInventoryModels } from "./SalesDeskInventoryModels";
import { filterSalesDeskInventoryModels } from "./salesDeskInventoryModel";
import { deskCard, deskEyebrow, deskMuted, deskSecondaryBtn } from "./salesDeskStyles";
import {
  filterSalesDeskLot,
  salesDeskLotCardTitle,
  salesDeskLotIntakeLabel,
  salesDeskLotOriginLabel,
  salesDeskLotShowsSerial,
  salesDeskShownCount,
  type SalesDeskShownEvent,
} from "./salesDeskLotFilter";
import { useSalesDeskInventoryModels } from "./useSalesDeskInventoryModels";
import { useSalesDeskLot } from "./useSalesDeskLot";

export function SalesDeskLot({
  locationLabel,
  onOpenSale,
  shownEvents,
  onShown,
}: {
  locationLabel: string;
  onOpenSale: (saleId: string) => void;
  shownEvents: SalesDeskShownEvent[];
  onShown: (unitId: string) => void;
}) {
  const inventory = useSalesDeskLot();
  const catalog = useSalesDeskInventoryModels();
  const [query, setQuery] = useState("");
  const units = useMemo(() => filterSalesDeskLot(inventory.units, query), [inventory.units, query]);
  const models = useMemo(() => filterSalesDeskInventoryModels(catalog.models, query), [catalog.models, query]);
  const place = locationLabel.trim();

  return (
    <div>
      <p className={deskEyebrow}>{place ? `My lot · ${place}` : "My lot"}</p>
      <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px]">Find the one they came for.</h1>
      <p className={`mt-3 max-w-[40rem] ${deskMuted}`}>Search by size, style, or serial.</p>
      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        aria-label="Search lot"
        placeholder="Search size, style or serial"
        className="mt-6 w-full rounded-lg border border-[#dce3dc] bg-white px-4 py-3 text-[#142d2d] outline-none dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
      />
      {inventory.error ? <p className="mt-4 text-sm text-red-800 dark:text-red-200">{inventory.error}</p> : null}
      {inventory.loading && inventory.units.length === 0 ? <p className={`mt-6 ${deskMuted}`}>Loading the lot…</p> : null}
      {!inventory.loading && !inventory.error && inventory.units.length === 0 ? (
        <p className={`mt-6 ${deskMuted}`}>No units on this lot yet.</p>
      ) : null}
      {!inventory.loading && inventory.units.length > 0 && units.length === 0 ? (
        <p className={`mt-6 ${deskMuted}`}>No matching units.</p>
      ) : null}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {units.map((unit) => {
          const serial = salesDeskLotShowsSerial(unit);
          const intake = salesDeskLotIntakeLabel(unit.intake_form_status);
          const origin = salesDeskLotOriginLabel(unit.origin);
          const shown = salesDeskShownCount(shownEvents, unit.id);
          return (
            <section key={unit.id} className={deskCard}>
              <div className="flex min-h-[180px] items-center justify-center rounded-[13px] bg-[#e7eef2] text-sm text-[#687772] dark:bg-zinc-800 dark:text-zinc-400">
                No photo
              </div>
              {unit.salePathLabel ? (
                <p className="mt-4 inline-flex rounded-full bg-[#eef1e7] px-2.5 py-1 text-[11px] font-semibold text-[#164b43] dark:bg-zinc-800 dark:text-emerald-200">
                  {unit.salePathLabel}
                </p>
              ) : null}
              <h2 className="mt-3 text-[21px] font-semibold tracking-[-0.4px]">{salesDeskLotCardTitle(unit)}</h2>
              {serial ? <p className="mt-1 break-all text-xs text-[#687772] dark:text-zinc-400">{serial}</p> : null}
              {origin ? <p className={`mt-2 ${deskMuted}`}>{origin}</p> : null}
              {intake ? (
                <p className="mt-3 inline-flex rounded-full bg-[#eef1e7] px-2.5 py-1 text-[11px] font-semibold text-[#164b43] dark:bg-zinc-800 dark:text-emerald-200">
                  {intake}
                </p>
              ) : null}
              {unit.notes ? <p className={`mt-3 line-clamp-3 ${deskMuted}`}>{unit.notes}</p> : null}
              <button type="button" onClick={() => onOpenSale(unit.id)} className={`${deskSecondaryBtn} mt-4 w-full`}>
                View sales record
              </button>
              <button type="button" onClick={() => onShown(unit.id)} className="mt-2 w-full py-2 text-sm font-semibold text-[#164b43] underline-offset-2 hover:underline dark:text-emerald-200">
                Shown to customer
              </button>
              {shown > 0 ? (
                <p className={`mt-1 ${deskMuted}`}>Shown this session · {shown}. Recorded separately from the sale.</p>
              ) : null}
            </section>
          );
        })}
      </div>
      <SalesDeskInventoryModels models={models} sourceLabel={catalog.sourceLabel} />
    </div>
  );
}
